import { inject, Injectable } from '@angular/core';
import { CmsApiService } from './api/cms-api.service';
import { combineLatest, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { CmsContentMapperService } from './cms-content-mapper.service';
import { filter, map, share, switchMap, tap } from 'rxjs/operators';
import { LanguageService } from './language/language.service';
import { ArrayService } from './array.service';
import { UserService } from './user/user.service';
import { isDesktop, isMobile } from '../helpers/device';
import { Payment } from '../vendor/ss-payments-v2/ss-payment-types';
import { SsPaymentsV2Service } from '../vendor/ss-payments-v2/ss-payment.service';
import { PlatformService } from 'ngx-unificator/services';

@Injectable({
  providedIn: 'root',
})
export class BankingService {
  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _languageService = inject(LanguageService);
  private _array = inject(ArrayService);
  private _user = inject(UserService);
  private _payment = inject(SsPaymentsV2Service);
  private _platform = inject(PlatformService);

  /**
   * All payment list
   */
  public paymentList$: Observable<any>;

  /**
   * Deposit payment list
   */
  public depositPaymentList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Withdrawal payment list
   */
  public withdrawalPaymentList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Custom Footer Images list
   */
  private _customImagesForFooterPayments = [
    'devcode-applepay',
    'devcode-interac',
    'devcode-picksellpay',
    'devcode-volt_bankuberweisung',
    'devcode-nodapay_uberweisungen',
    'devcode-funanga',
    'devcode-bank_transfer',
  ];

  getAllPayments(isBankingPage = false) {
    if (!this._platform.isBrowser && !isBankingPage) {
      this.paymentList$ = of(null);
      return;
    }
    this.paymentList$ = this._languageService.langChange$.pipe(
      switchMap(() => this._user.auth$),
      switchMap((auth) => {
        if (!auth) {
          return forkJoin([this.getPaymentMethodsByType('deposit', Number(this._user.auth)), this.getPaymentMethodsByType('cashout', Number(this._user.auth))]);
        } else {
          return combineLatest([
            this.getPaymentMethodsByTypeFromPaymentLib(Payment.Action.Deposit),
            this.getPaymentMethodsByTypeFromPaymentLib(Payment.Action.Cashout),
          ]);
        }
      }),
      tap((list) => {
        if (this._platform.isBrowser) {
          list[0] = list[0].filter((e) => (isDesktop() && !e.HideOnDesk) || isMobile());
          list[1] = list[1].filter((e) => (isDesktop() && !e.HideOnDesk) || isMobile());
        }
        this.depositPaymentList$.next(list?.[0]);
        this.withdrawalPaymentList$.next(list[1]);
      }),
      map(([dep, cash]) => this._array.uniqueBy('id', [...dep, ...cash])),
      share(),
    );
  }

  /**
   * Get payment methods from CMS by type
   */
  getPaymentMethodsByType(type: string, sort_auth_player: number) {
    return this._api.paymentList({ type, sort_auth_player }).pipe(
      map((response) =>
        this._mapper.mapCmsData(response.data, {
          id: 'id',
          slug: 'slug',
        }),
      ),
      map((list: any[]) => list.filter((e) => e.id)),
      map((payments) => {
        return payments.map((e) => {
          e.img = e.Image;
          e.footerImg = 'https://contentdeliverynetwork.cc/logos/payments/white/' + e.slug + '.svg';
          e.ourImg = `/assets/svg/payment-methods-new/${e.slug}.svg`;
          e.isInterac = e?.slug?.includes('interac') || e?.brand?.includes('interac');
          return e;
        });
      }),
      map((list) => {
        const resultArr = this._customImagesForFooterPayments.map((elem) => list.find((e) => e.slug === elem)).filter((e) => !!e);
        if (resultArr[0]) {
          resultArr.map((e) => (e.footerImg = `/assets/svg/payment-methods-new/${e.slug}-custom.svg`));
        }
        return list;
      }),
    );
  }

  getPaymentMethodsByTypeFromPaymentLib(paymentAction: Payment.Action) {
    return this._user.currency$.pipe(
      filter((currency) => !!currency),
      switchMap((currency) => this._payment.fetchMethods(currency, paymentAction).pipe(map((list) => this._mapPlatformPayment(list, paymentAction)))),
    );
  }

  private _mapPlatformPayment(list: any[], bankingType: Payment.Action): any[] {
    return list
      ?.map((item) => {
        return {
          id: item?.id,
          IsCrypto: item?.type === 'crypto',
          DepositLimit: this._resolveLimits(item!),
          Withdrawals: this._resolveLimits(item!),
          img: item?.ourImg,
          ourImg: item.iconSrc,
          footerImg: item?.iconSrc,
          isInterac: item?.slug?.includes('interac') || item?.brand?.includes('interac'),
          Fees:
            item?.termsOfService?.commissions?.type !== 'none'
              ? (this._user.currentCurrency?.symbol || this._user.currentCurrency?.currency) + ' ' + item[bankingType]?.commission_fixed
              : '0%',
        };
      })
      .sort((a, b) => Number(a.IsCrypto) - Number(b.IsCrypto));
  }

  private _resolveLimits(method: any) {
    return method?.termsOfService?.restrictions?.minAmountValue && method?.termsOfService?.restrictions?.maxAmountValue
      ? `${method?.termsOfService?.restrictions?.minAmountValue} ${this._user.currentCurrency?.symbol || this._user.currentCurrency?.currency} - ${method?.termsOfService?.restrictions?.maxAmountValue} ${this._user.currentCurrency?.symbol || this._user.currentCurrency?.currency}`
      : method?.termsOfService?.restrictions?.minAmountValue && !method?.termsOfService?.restrictions?.maxAmountValue
        ? `${method?.termsOfService?.restrictions?.minAmountValue} ${this._user.currentCurrency?.symbol || this._user.currentCurrency?.currency}`
        : null;
  }
}
